import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Check, Close, KeyboardArrowUp, Refresh, Search } from '@mui/icons-material';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChipInput, ConfirmDialog, CreatableSelect, CsvTemplateHint, EmptyState, StatusChip } from './ui';
import { taxonomyTemplateProps } from './mapping/taxonomyTemplate';
import TaxonomyCategorySelect from './mapping/TaxonomyCategorySelect';
import ShopifyCategoryPicker from './mapping/ShopifyCategoryPicker';
import { useShopifyTaxonomyOptions } from './mapping/useShopifyTaxonomyOptions';
import { useAppTranslation } from '../i18n/useAppTranslation';
import {
  ClassifierTreeNode,
  integrationApiService,
  MerchantTaxonomyEntry,
  MerchantTaxonomyImportPreview,
  MerchantTaxonomyPatch,
  MerchantTaxonomyRegistry,
  MerchantTaxonomyStats,
  ShopifyCategorySummary,
  ShopifyFieldOwner,
  StoreLayoutSettings,
  TaxonomyClassifierResult,
  UnmatchedTaxonomyProduct,
} from '../services/integrationApi';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { LENS_FOCAL_CATEGORY_KEYS } from '../constants/lensFocalCategoryKeys';
import CategorySlugLabel from './mapping/CategorySlugLabel';
import { useToast } from '../contexts/ToastContext';
import { useUnsavedChangesSource } from './appUpdate';
import { tokens } from '../theme/tokens';
import { categorySlugCounts, countPrefixMatches, MappingIndexRow, ruleMatchCounts, suggestKeywords, suggestSkuPrefixes } from '../utils/mappingIndex';
import {
  FallbackMode,
  categorySlugs,
  fallbackLabel,
  fallbackModeOf,
  normalizeMotzuSlug,
  resolveStoreProductType,
  resolveTaxonomyForSlug,
  taxonomyLabel,
  UNMAPPED_CATEGORY_UNSET,
  UNMAPPED_CATEGORY_UNSET_LABEL,
  UNMAPPED_CATEGORY_UNSET_SHORT,
  unmappedCategoryIsUnset,
  unmappedCategoryNote,
  unmappedFallbackGid,
  usesTaxonomyDefaults,
  withFallbackMode,
  withTaxonomyDefaults,
} from './mapping/mappingModel';

interface ShopifyCategoryMappingSectionProps {
  /** Needed for Discover; omitted while the integration is still being created. */
  integrationId?: string;
  layout: StoreLayoutSettings | undefined;
  onChange: (layout: StoreLayoutSettings) => void;
  /** Kept so a parent that persisted a discovery itself can refresh without marking dirty. */
  onApplied?: (layout: StoreLayoutSettings) => void;
}

type ProductTypeRule = NonNullable<StoreLayoutSettings['productTypeRules']>[number];
type DiscoveredLayout = {
  categoryMap?: Record<string, string>;
  productTypesInUse?: string[];
  collections?: StoreLayoutSettings['collections'];
  menuStructure?: StoreLayoutSettings['menuStructure'];
  allMotzuCategorySlugs?: string[];
};

type SlugFilter = 'all' | 'unmapped' | 'mapped' | 'suggested';

const cardSx = {
  bgcolor: tokens.surface.card,
  border: '1px solid',
  borderColor: tokens.line.default,
  borderRadius: `${tokens.radius.panel}px`,
  boxShadow: tokens.shadow.card,
} as const;

const nonEmpty = (values: unknown[]): string[] =>
  values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

const mono = { fontFamily: tokens.font.mono, fontSize: 12.5 };

/** Checkbox | category | product type | Shopify category | source; on a phone the rest wrap under the category. */
const CATEGORY_TABLE_COLUMNS = { xs: '36px minmax(0,1fr)', md: '36px minmax(0,0.9fr) minmax(0,1fr) minmax(0,1fr) 112px' };
const UNDER_SLUG_ON_PHONE = { gridColumn: { xs: '2 / -1', md: 'auto' } };
/** Subcategory | SKU prefix | Shopify category | product type; on a phone each field stacks. */
const TAXONOMY_TABLE_COLUMNS = { xs: 'minmax(0,1fr)', md: 'minmax(0,1.1fr) 130px minmax(0,1fr) minmax(0,1fr)' };
const MERCHANT_TAXONOMY_QUERY_KEY = ['merchant-taxonomy'] as const;
const MAPPING_INDEX_QUERY_KEY = ['mapping-index'] as const;

/**
 * Which Shopify product_type each Motzu category gets: the category map,
 * name rules, and one fallback. Discovery proposes rows; nothing is written
 * until the parent saves.
 */
const ShopifyCategoryMappingSection: React.FC<ShopifyCategoryMappingSectionProps> = ({
  integrationId,
  layout,
  onChange,
}) => {
  const current = layout || {};
  const { enabled: ownershipFlag } = useOrgFeatureFlag('shopify_field_ownership');
  const { enabled: ruleCategoryEnabled } = useOrgFeatureFlag('name_rule_shopify_category');
  // Flag `lens_focal_range`: rows for a lens by prime/zoom and focal range (MOT-452).
  const { enabled: lensFocalRangeOn } = useOrgFeatureFlag('lens_focal_range');
  // Flag `shopify_unmapped_category_unset`: "Don't set" for the unmatched-category select, same as the v2 FallbackStep.
  const { enabled: unsetAllowed } = useOrgFeatureFlag('shopify_unmapped_category_unset');
  const unsetChosen = unmappedCategoryIsUnset(current, unsetAllowed);
  /**
   * What a map row with no override falls back to. With "Don't set" chosen (flag on) that is no category at all,
   * so the row says so instead of naming the neutral root the sync will not send.
   */
  const rowCategoryDefault = (slug: string): { defaultGid: string | null; defaultName?: string } => {
    const resolved = resolveTaxonomyForSlug(slug, { ...current, taxonomyCategoryMap: {} }, categoryDefaults.data, {
      unmappedUnset: unsetAllowed,
    });
    if (resolved?.step === 'unset') return { defaultGid: null, defaultName: UNMAPPED_CATEGORY_UNSET_SHORT };
    return { defaultGid: resolved?.gid ?? null };
  };
  // Flag `structured_mapping_inputs`: pick-lists and match counts from the products in the sync selection.
  const { enabled: structuredFlag } = useOrgFeatureFlag('structured_mapping_inputs');
  const mappingIndex = useQuery({
    queryKey: [...MAPPING_INDEX_QUERY_KEY, integrationId],
    queryFn: () => integrationApiService.getMappingIndex(integrationId as string),
    enabled: structuredFlag && Boolean(integrationId),
    retry: false,
  });
  const indexRows = structuredFlag ? mappingIndex.data?.products : undefined;
  const categoryMap = current.categoryMap || {};
  const rules = current.productTypeRules || [];
  const enabled = current.useDiscoveredMapping !== false;
  // "Use a default type" is chosen before a type is typed; with no type saved it still derives.
  const [defaultPicked, setDefaultPicked] = useState(false);
  const storedMode = fallbackModeOf(current);
  const mode: FallbackMode = storedMode === 'derive' && defaultPicked ? 'default' : storedMode;

  const [extraSlugs, setExtraSlugs] = useState<string[]>([]);
  const [filter, setFilter] = useState<SlugFilter>('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkType, setBulkType] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState<DiscoveredLayout | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testName, setTestName] = useState('');
  const [testCategory, setTestCategory] = useState('');

  const patch = (partial: Partial<StoreLayoutSettings>) => onChange({ ...current, ...partial });
  const setMap = (next: Record<string, string>) => patch({ categoryMap: next });
  const setRules = (next: ProductTypeRule[]) => patch({ productTypeRules: next });

  const slugs = useMemo(
    () => categorySlugs(current, extraSlugs, lensFocalRangeOn ? LENS_FOCAL_CATEGORY_KEYS : []),
    [current, extraSlugs, lensFocalRangeOn],
  );

  // "Add a category not listed": with the index, the catalog categories in the selection the map lacks, with counts.
  const newSlugOptions = useMemo(() => {
    if (!indexRows) return current.allMotzuCategorySlugs || [];
    const listed = new Set([...slugs, ...Object.keys(categoryMap)]);
    return Array.from(categorySlugCounts(indexRows).entries())
      .filter(([slug]) => !listed.has(slug))
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([slug, count]) => ({ value: slug, hint: `matches ${count} product${count === 1 ? '' : 's'}` }));
  }, [indexRows, slugs, categoryMap, current.allMotzuCategorySlugs]);
  const ruleCounts = useMemo(() => (indexRows ? ruleMatchCounts(indexRows, rules) : null), [indexRows, rules]);
  const keywordSuggestions = useMemo(() => (indexRows ? suggestKeywords(indexRows, rules) : undefined), [indexRows, rules]);

  // Always loaded: a new store has discovered nothing yet but can still see what each category syncs to.
  const categoryDefaults = useQuery({
    queryKey: ['shopify-category-defaults'],
    queryFn: () => integrationApiService.getShopifyCategoryDefaults(),
    staleTime: Infinity,
    retry: false,
  });
  const curatedTaxonomyOptions = categoryDefaults.data?.options;
  const taxonomyMap = current.taxonomyCategoryMap || {};
  const setTaxonomy = (slug: string, gid: string) => {
    const next = { ...taxonomyMap };
    if (gid) next[slug] = gid;
    else delete next[slug];
    patch({ taxonomyCategoryMap: next });
  };
  const taxonomyOwnCount = Object.keys(taxonomyMap).length;

  // Flag `merchant_taxonomy`: the store's own registry outranks the category map below it.
  const { enabled: merchantTaxonomyOn } = useOrgFeatureFlag('merchant_taxonomy');
  const merchantTaxonomy = useQuery({
    queryKey: MERCHANT_TAXONOMY_QUERY_KEY,
    queryFn: () => integrationApiService.getMerchantTaxonomy(),
    enabled: merchantTaxonomyOn,
    retry: false,
  });
  const merchantRegistry = merchantTaxonomyOn ? merchantTaxonomy.data?.registry ?? null : null;
  // A category outside the curated list (picked from the full taxonomy) is labelled by its breadcrumb.
  const taxonomyOptions = useShopifyTaxonomyOptions(
    useMemo(() => curatedTaxonomyOptions || [], [curatedTaxonomyOptions]),
    useMemo(
      () => [
        ...(merchantRegistry?.entries || []).map((entry) => entry.shopifyCategoryGid),
        ...Object.values(current.taxonomyCategoryMap || {}),
        ...(current.productTypeRules || []).map((rule) => rule.taxonomyCategoryGid),
        current.unmappedTaxonomyCategory,
      ],
      [merchantRegistry, current.taxonomyCategoryMap, current.productTypeRules, current.unmappedTaxonomyCategory],
    ),
  );

  const typeOptions = useMemo(
    () =>
      Array.from(
        new Set(nonEmpty([
          ...(current.productTypesInUse || []),
          ...(discovered?.productTypesInUse || []),
          ...Object.values(categoryMap),
          ...rules.map((rule) => rule.productType),
          current.defaultProductType,
        ])),
      ).sort((a, b) => a.localeCompare(b)),
    [current.productTypesInUse, current.defaultProductType, categoryMap, rules, discovered],
  );

  const suggestions = useMemo(() => {
    const proposed = discovered?.categoryMap || {};
    const next: Record<string, string> = {};
    for (const [slug, type] of Object.entries(proposed)) {
      if (typeof type === 'string' && type.trim() && categoryMap[slug] !== type) next[slug] = type;
    }
    return next;
  }, [discovered, categoryMap]);

  const suggestionCount = Object.keys(suggestions).length;
  const mappedCount = slugs.filter((slug) => Boolean(categoryMap[slug]?.trim())).length;
  const fallbackCount = slugs.length - mappedCount;

  const visibleSlugs = slugs.filter((slug) => {
    if (query.trim() && !slug.includes(query.trim().toLowerCase())) return false;
    if (filter === 'mapped') return Boolean(categoryMap[slug]?.trim());
    if (filter === 'unmapped') return !categoryMap[slug]?.trim();
    if (filter === 'suggested') return Boolean(suggestions[slug]);
    return true;
  });

  const selectedVisible = visibleSlugs.filter((slug) => selected.has(slug));
  const allVisibleSelected = visibleSlugs.length > 0 && selectedVisible.length === visibleSlugs.length;
  const someVisibleSelected = selectedVisible.length > 0 && !allVisibleSelected;

  const discover = async () => {
    if (!integrationId) return;
    setDiscovering(true);
    setError(null);
    try {
      const result = await integrationApiService.discoverStoreLayout(integrationId);
      setDiscovered(result.discovered || {});
      setBannerDismissed(false);
      setFilter('all');
    } catch (err: unknown) {
      setError(`Discovery failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDiscovering(false);
    }
  };

  const acceptSuggestions = (onlySlug?: string) => {
    if (!discovered) return;
    const nextMap = { ...categoryMap };
    for (const [slug, type] of Object.entries(suggestions)) {
      if (onlySlug && slug !== onlySlug) continue;
      nextMap[slug] = type;
    }
    patch({
      categoryMap: nextMap,
      productTypesInUse: discovered.productTypesInUse || current.productTypesInUse,
      allMotzuCategorySlugs: discovered.allMotzuCategorySlugs || current.allMotzuCategorySlugs,
      collections: discovered.collections || current.collections,
      menuStructure: discovered.menuStructure || current.menuStructure,
      discoveredAt: new Date().toISOString(),
    });
    if (!onlySlug) {
      setDiscovered(null);
      setBannerDismissed(true);
    }
  };

  const applyBulk = () => {
    const target = bulkType.trim();
    if (!target || selectedVisible.length === 0) return;
    const next = { ...categoryMap };
    for (const slug of selectedVisible) next[slug] = target;
    setMap(next);
    setSelected(new Set());
    setBulkType('');
  };

  const addSlug = () => {
    const slug = normalizeMotzuSlug(newSlug);
    if (!slug) return;
    setExtraSlugs((prev) => (prev.includes(slug) ? prev : [slug, ...prev]));
    const base = current.allMotzuCategorySlugs?.length ? current.allMotzuCategorySlugs : categorySlugs(current);
    if (!base.includes(slug)) patch({ allMotzuCategorySlugs: [slug, ...base] });
    setNewSlug('');
    setFilter('all');
    setQuery('');
  };

  const setMode = (next: FallbackMode) => {
    setDefaultPicked(next === 'default');
    onChange(withFallbackMode(current, next, next === 'default' ? current.defaultProductType : undefined));
  };

  const preview = testName.trim() || testCategory.trim()
    ? resolveStoreProductType({
      name: testName,
      category: testCategory,
      enabled,
      categoryMap,
      rules,
      mode,
      defaultProductType: current.defaultProductType,
    })
    : null;

  const filters: Array<{ id: SlugFilter; label: string; count: number; tone?: 'caution' | 'info' }> = [
    { id: 'all', label: 'All', count: slugs.length },
    { id: 'unmapped', label: 'Unmapped', count: fallbackCount, tone: 'caution' },
    { id: 'mapped', label: 'Mapped', count: mappedCount },
    ...(suggestionCount > 0 ? [{ id: 'suggested' as const, label: 'Suggested', count: suggestionCount, tone: 'info' as const }] : []),
  ];

  // Flag `shopify_mapping_simple_mode`: a short summary first, the levers behind "Advanced settings".
  // Default view: simple, unless the store already runs a registry. Flag off: the tab is exactly as before.
  const { enabled: simpleModeFlag } = useOrgFeatureFlag('shopify_mapping_simple_mode');
  const simpleMode = simpleModeFlag && Boolean(integrationId);
  const mappingView = current.mappingView ?? (merchantRegistry ? 'advanced' : 'simple');
  const showAdvanced = !simpleMode || mappingView === 'advanced';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {simpleMode && integrationId && (
        <SimpleCategoryCard
          integrationId={integrationId}
          layout={current}
          onChange={patch}
          registry={merchantRegistry}
          taxonomyOptions={taxonomyOptions}
          neutralGid={categoryDefaults.data?.neutralGid}
          ownershipShown={ownershipFlag}
          advancedOpen={showAdvanced}
          onSetView={(view) => patch({ mappingView: view })}
        />
      )}
      {showAdvanced && (
        <>
      <Box sx={{ ...cardSx, px: 2.75, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: tokens.ink.primary }}>Category → product type</Typography>
            <Typography sx={{ fontSize: 13.5, color: tokens.ink.muted, maxWidth: 640, mt: 0.5 }}>
              Which Shopify product type each Motzu category is synced as. {ruleCategoryEnabled ? 'A matching name rule wins over this map.' : 'A name rule wins when the category has no row.'} Anything still open uses the fallback.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tokens.ink.body }}>
                {enabled ? 'Applied to product_type' : 'Not applied (off)'}
              </Typography>
            </Box>
            <Switch
              size="small"
              checked={enabled}
              onChange={(event) => patch({ useDiscoveredMapping: event.target.checked })}
              inputProps={{ 'aria-label': 'Apply this mapping to product type' }}
            />
            {integrationId && (
              <>
                <Box sx={{ width: '1px', alignSelf: 'stretch', bgcolor: tokens.line.default }} />
                <Button
                  variant="outlined"
                  startIcon={discovering ? <CircularProgress size={14} /> : <Refresh />}
                  disabled={discovering}
                  onClick={discover}
                  sx={{ height: 36, borderColor: tokens.line.default, borderRadius: '9px', color: tokens.ink.body, fontWeight: 600 }}
                >
                  Re-discover store layout
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
            {mappedCount} of {slugs.length} categories mapped
          </Typography>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.signal.caution.ink }}>
            {fallbackCount} use the fallback
          </Typography>
          <Typography sx={{ ml: 'auto', fontSize: 12.5, color: tokens.ink.faint }}>
            {current.discoveredAt ? `Last discovered ${new Date(current.discoveredAt).toLocaleDateString()}` : 'Not discovered yet'}
            {typeOptions.length > 0 ? ` · ${current.productTypesInUse?.length || typeOptions.length} Shopify product types in store` : ''}
          </Typography>
        </Box>
        <Box sx={{ height: 6, borderRadius: 99, bgcolor: tokens.signal.caution.tint, overflow: 'hidden' }}>
          <Box
            sx={{
              height: '100%',
              width: slugs.length ? `${(mappedCount / slugs.length) * 100}%` : 0,
              bgcolor: tokens.accent.main,
              transition: 'width 250ms ease',
            }}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', sm: 'repeat(auto-fit, minmax(220px, 1fr))' }, gap: 1.25 }}>
          {merchantRegistry && merchantRegistry.entries.length > 0 && (
            <ResolutionCard
              n="0"
              title={`Your taxonomy · ${merchantTaxonomy.data?.stats.mapped ?? 0}`}
              body="The store SKU's prefix decides, before anything below."
              tone="info"
            />
          )}
          <ResolutionCard
            n="1"
            title={`Category map · ${mappedCount}`}
            body="The product's Motzu category decides."
            tone="info"
          />
          <ResolutionCard
            n="2"
            title={`Name rules · ${rules.filter((rule) => rule.keywords.some((kw) => kw.trim()) && rule.productType.trim()).length}`}
            body="A keyword in the name, when the category has no type."
            tone="info"
          />
          <ResolutionCard
            n="3"
            title="Fallback"
            body={
              mode === 'flag'
                ? 'Unmapped categories are prefixed with [UNMAPPED].'
                : mode === 'default'
                  ? (current.defaultProductType?.trim()
                    ? `Every unmapped category becomes “${current.defaultProductType.trim()}”.`
                    : 'Choose a default type.')
                  : 'Uses the category name, e.g. prime-macro → Prime Macro.'
            }
            tone="caution"
          />
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {suggestionCount > 0 && !bannerDismissed && (
        <Box
          sx={{
            ...cardSx,
            bgcolor: tokens.accent.tint,
            borderColor: tokens.accent.border,
            px: 2.25,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 13.5, color: tokens.ink.body, flex: '1 1 280px' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>Discovery found {suggestionCount} suggestions</Box>
            {' '}from your store&apos;s product types. They&apos;re marked in the list below; nothing changes until you accept.
          </Typography>
          <Button size="small" onClick={() => setFilter('suggested')} sx={{ fontWeight: 600 }}>Review</Button>
          <Button size="small" variant="contained" onClick={() => acceptSuggestions()} sx={{ fontWeight: 600 }}>Accept all</Button>
          <Button size="small" onClick={() => setBannerDismissed(true)} sx={{ fontWeight: 600 }}>Dismiss</Button>
        </Box>
      )}

      {merchantTaxonomyOn && (
        <MerchantTaxonomyCard
          registry={merchantRegistry}
          stats={merchantTaxonomy.data?.stats}
          loading={merchantTaxonomy.isLoading}
          taxonomyOptions={taxonomyOptions}
          typeOptions={typeOptions}
          indexRows={indexRows}
          integrationId={integrationId}
        />
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ ...cardSx, flex: '2 1 560px', minWidth: 0, overflow: 'hidden' }}>
          <Box sx={{ px: 2.25, pt: 2, pb: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Category map</Typography>
          </Box>
          <Box sx={{ px: 2.25, pb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', p: 0.5, bgcolor: tokens.surface.tint, borderRadius: '10px', gap: 0.25 }}>
              {filters.map((item) => {
                const active = filter === item.id;
                return (
                  <Button
                    key={item.id}
                    onClick={() => setFilter(item.id)}
                    sx={{
                      minWidth: 0,
                      height: 28,
                      px: 1.25,
                      borderRadius: '8px',
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: active ? tokens.ink.primary : tokens.ink.muted,
                      bgcolor: active ? tokens.surface.card : 'transparent',
                      boxShadow: active ? tokens.shadow.card : 'none',
                    }}
                  >
                    {item.label}
                    <Box
                      component="span"
                      sx={{
                        ml: 0.75,
                        color: item.tone === 'caution'
                          ? tokens.signal.caution.ink
                          : item.tone === 'info'
                            ? tokens.accent.main
                            : tokens.ink.faint,
                      }}
                    >
                      {item.count}
                    </Box>
                  </Button>
                );
              })}
            </Box>
            <TextField
              size="small"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find a category slug"
              slotProps={{
                input: {
                  startAdornment: <Search sx={{ fontSize: 16, color: tokens.ink.faint, mr: 0.75 }} />,
                },
              }}
              sx={{ flex: '1 1 180px', '& .MuiInputBase-root': { height: 34 } }}
            />
          </Box>

          {selectedVisible.length > 0 && (
            <Box
              sx={{
                mx: 2.25,
                mb: 1.25,
                px: 1.5,
                py: 1,
                borderRadius: '10px',
                bgcolor: tokens.surface.header,
                color: tokens.ink.inverse,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{selectedVisible.length} selected</Typography>
              <Typography sx={{ fontSize: 13, color: tokens.ink.onDark }}>Map to</Typography>
              <Box
                sx={{ flex: '1 1 180px', minWidth: 160, '& .MuiInputBase-root': { bgcolor: tokens.surface.card } }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && bulkType.trim()) {
                    event.preventDefault();
                    applyBulk();
                  }
                }}
              >
                <CreatableSelect
                  label=""
                  aria-label="Map selected categories to"
                  value={bulkType}
                  options={typeOptions}
                  onChange={setBulkType}
                  placeholder="Product type"
                />
              </Box>
              <Button
                size="small"
                variant="contained"
                disabled={!bulkType.trim()}
                onClick={applyBulk}
                sx={{ fontWeight: 700, bgcolor: bulkType.trim() ? tokens.accent.main : tokens.ink.body }}
              >
                Apply
              </Button>
              <Button size="small" onClick={() => setSelected(new Set())} sx={{ color: tokens.ink.onDark, fontWeight: 600 }}>
                Clear
              </Button>
            </Box>
          )}

          <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: CATEGORY_TABLE_COLUMNS, px: 1.5, height: 34, alignItems: 'center', bgcolor: tokens.surface.sunken }}>
            <Checkbox
              size="small"
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected}
              onChange={() => {
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (allVisibleSelected) visibleSlugs.forEach((slug) => next.delete(slug));
                  else visibleSlugs.forEach((slug) => next.add(slug));
                  return next;
                });
              }}
              inputProps={{ 'aria-label': 'Select visible categories' }}
            />
            {['Motzu category', 'Shopify product type', 'Shopify category', 'Source'].map((label) => (
              <Typography key={label} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}>
                {label}
              </Typography>
            ))}
          </Box>
          <Box sx={{ maxHeight: 560, overflow: 'auto' }}>
            {visibleSlugs.length === 0 && (
              <Typography sx={{ px: 2, py: 2, fontSize: 13.5, color: tokens.ink.muted }}>
                {query.trim() ? `No category matches “${query.trim()}”.` : filter === 'unmapped' ? 'Every category is mapped.' : 'No categories in this view.'}
              </Typography>
            )}
            {visibleSlugs.map((slug) => {
              const mapped = Boolean(categoryMap[slug]?.trim());
              const suggestion = suggestions[slug];
              return (
                <Box
                  key={slug}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: CATEGORY_TABLE_COLUMNS,
                    alignItems: 'center',
                    minHeight: 48,
                    px: 1.5,
                    borderBottom: '1px solid',
                    borderColor: tokens.line.hairline,
                    bgcolor: selected.has(slug) ? tokens.signal.info.row : 'transparent',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={selected.has(slug)}
                    onChange={() => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(slug)) next.delete(slug);
                        else next.add(slug);
                        return next;
                      });
                    }}
                    inputProps={{ 'aria-label': `Select ${slug}` }}
                  />
                  <CategorySlugLabel slug={slug} monoSx={mono} lensFocalRange={lensFocalRangeOn} />
                  <Box sx={{ pr: 1, minWidth: 0, ...UNDER_SLUG_ON_PHONE }}>
                    <CreatableSelect
                      label=""
                      aria-label={`Product type for ${slug}`}
                      value={categoryMap[slug] || ''}
                      options={typeOptions}
                      onChange={(value) => {
                        if (!value.trim()) {
                          const next = { ...categoryMap };
                          delete next[slug];
                          setMap(next);
                          return;
                        }
                        setMap({ ...categoryMap, [slug]: value });
                      }}
                      placeholder={`Falls back to ${fallbackLabel(slug, mode, current.defaultProductType)}`}
                      sx={categoryMap[slug] ? undefined : {
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.signal.caution.border },
                      }}
                    />
                    {suggestion && (
                      <Button
                        size="small"
                        startIcon={<Check sx={{ fontSize: 14 }} />}
                        onClick={() => acceptSuggestions(slug)}
                        sx={{
                          mt: 0.5,
                          height: 24,
                          border: '1px dashed',
                          borderColor: tokens.accent.border,
                          bgcolor: tokens.accent.tint,
                          color: tokens.accent.hover,
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {suggestion}
                      </Button>
                    )}
                  </Box>
                  <Box sx={{ pr: 1, minWidth: 0, ...UNDER_SLUG_ON_PHONE }}>
                    <TaxonomyCategorySelect
                      aria-label={`Shopify category for ${slug}`}
                      value={taxonomyMap[slug] || ''}
                      {...rowCategoryDefault(slug)}
                      options={taxonomyOptions}
                      onChange={(gid) => setTaxonomy(slug, gid)}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...UNDER_SLUG_ON_PHONE }}>
                    <Box
                      sx={{
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '6px',
                        fontSize: 11.5,
                        fontWeight: 700,
                        bgcolor: mapped ? tokens.signal.positive.tint : tokens.signal.caution.tint,
                        color: mapped ? tokens.signal.positive.ink : tokens.signal.caution.ink,
                      }}
                    >
                      {mapped ? 'Mapped' : 'Fallback'}
                    </Box>
                    {mapped && (
                      <IconButton
                        size="small"
                        aria-label={`Remove mapping for ${slug}`}
                        onClick={() => {
                          const next = { ...categoryMap };
                          delete next[slug];
                          setMap(next);
                        }}
                      >
                        <Close sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', px: 2, py: 1.25, borderTop: '1px solid', borderColor: tokens.line.hairline }}>
            <Box sx={{ flex: 1, minWidth: 0, '& input': mono }}>
              <CreatableSelect
                label=""
                aria-label="Add a category not listed"
                value={newSlug}
                options={newSlugOptions}
                onChange={setNewSlug}
                placeholder="Add a category not listed, e.g. gimbal"
              />
            </Box>
            <Button size="small" variant="outlined" disabled={!normalizeMotzuSlug(newSlug)} onClick={addSlug} sx={{ fontWeight: 600 }}>
              Add
            </Button>
          </Box>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ ...cardSx, p: 2.25 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>Name rules</Typography>
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mb: 1.5 }}>
              {ruleCategoryEnabled ? 'If the product name contains the keyword, it gets that type, ahead of the category map.' : 'If the product name contains the keyword, it gets that type when the category is unmapped.'} Top to bottom, first match wins.
              {ruleCategoryEnabled && ' Also sets the Shopify Category for products this rule matches. Leave empty to keep the category mapping. A category here replaces the one on the listing, including in Fill gaps.'}
            </Typography>
            {rules.map((rule, ruleIdx) => (
              <Box key={ruleIdx} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', mb: 1 }}>
                <Typography sx={{ width: 18, pt: 1, fontSize: 12, fontWeight: 700, color: tokens.ink.faint }}>{ruleIdx + 1}</Typography>
                <Box sx={{ flex: '1 1 100px', minWidth: 0 }}>
                  <ChipInput
                    aria-label={`Rule ${ruleIdx + 1} keyword`}
                    placeholder="Keyword"
                    value={rule.keywords}
                    suggestions={keywordSuggestions}
                    onChange={(keywords) => {
                      const next = [...rules];
                      next[ruleIdx] = { ...rule, keywords };
                      setRules(next);
                    }}
                  />
                </Box>
                {ruleCounts && (
                  <Box sx={{ pt: 0.5 }}>
                    <StatusChip label={`matches ${ruleCounts[ruleIdx] ?? 0}`} tone={ruleCounts[ruleIdx] ? 'success' : 'neutral'} />
                  </Box>
                )}
                <Box sx={{ flex: '1 1 120px', minWidth: 0 }}>
                  <CreatableSelect
                    label=""
                    aria-label={`Rule ${ruleIdx + 1} product type`}
                    value={rule.productType}
                    options={typeOptions}
                    onChange={(value) => {
                      const next = [...rules];
                      next[ruleIdx] = { ...rule, productType: value };
                      setRules(next);
                    }}
                    placeholder="Type"
                  />
                </Box>
                {ruleCategoryEnabled && (
                  <Box sx={{ flex: '1 1 140px', minWidth: 0 }}>
                    <TaxonomyCategorySelect
                      aria-label={`Rule ${ruleIdx + 1} Shopify category`}
                      value={rule.taxonomyCategoryGid || ''}
                      defaultGid={null}
                      defaultLabelPrefix="Category mapping"
                      options={taxonomyOptions}
                      onChange={(gid) => {
                        const next = [...rules];
                        const { taxonomyCategoryGid: _previous, ...rest } = rule;
                        next[ruleIdx] = gid ? { ...rest, taxonomyCategoryGid: gid } : rest;
                        setRules(next);
                      }}
                    />
                  </Box>
                )}
                <IconButton
                  size="small"
                  aria-label="Move rule up"
                  disabled={ruleIdx === 0}
                  onClick={() => {
                    const next = [...rules];
                    const [row] = next.splice(ruleIdx, 1);
                    next.splice(ruleIdx - 1, 0, row);
                    setRules(next);
                  }}
                  sx={{ opacity: ruleIdx === 0 ? 0.3 : 1 }}
                >
                  <KeyboardArrowUp fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Remove rule"
                  onClick={() => setRules(rules.filter((_, index) => index !== ruleIdx))}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ))}
            <Button size="small" onClick={() => setRules([...rules, { keywords: [], productType: '' }])} sx={{ fontWeight: 600 }}>
              Add rule
            </Button>
          </Box>

          {ownershipFlag && (
            <Box sx={{ ...cardSx, p: 2.25 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>Who owns these fields</Typography>
              <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mb: 1.5 }}>
                Motzu owns: Motzu sets it the way your sync mode does today. Fill only when blank keeps anything your team or ERP set. Never touch leaves it alone. Choose Fill only when blank if your ERP sets these fields.
              </Typography>
              <OwnerChoice
                label="Shopify Category"
                value={current.shopifyCategoryOwner}
                onChange={(owner) => patch({ shopifyCategoryOwner: owner })}
              />
              <OwnerChoice
                label="Product Type"
                value={current.shopifyProductTypeOwner}
                onChange={(owner) => patch({ shopifyProductTypeOwner: owner })}
              />
            </Box>
          )}

          <Box sx={{ ...cardSx, p: 2.25 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.25 }}>Fallback for unmapped</Typography>
            <FallbackChoice
              selected={mode === 'derive'}
              title="Derive from category"
              body="Uses the category name, e.g. prime-macro → Prime Macro."
              onSelect={() => setMode('derive')}
            />
            <FallbackChoice
              selected={mode === 'default'}
              title="Use a default type"
              body="Every unmapped category gets the same type."
              onSelect={() => setMode('default')}
            >
              {mode === 'default' && (
                <Box sx={{ mt: 1 }}>
                  <CreatableSelect
                    label="Default product type"
                    value={current.defaultProductType || ''}
                    options={typeOptions}
                    onChange={(value) => onChange(withFallbackMode(current, 'default', value))}
                    placeholder="e.g. Accessories"
                    helperText={current.defaultProductType?.trim() ? undefined : 'Until you pick a type, unmapped categories keep deriving from their category.'}
                  />
                </Box>
              )}
            </FallbackChoice>
            <FallbackChoice
              selected={mode === 'flag'}
              title="Flag as [UNMAPPED]"
              body="Prefix the type so you can find and fix these in Shopify."
              onSelect={() => setMode('flag')}
            />
          </Box>

          <Box sx={{ ...cardSx, p: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Shopify category</Typography>
              <Button
                size="small"
                disabled={usesTaxonomyDefaults(current)}
                onClick={() => onChange(withTaxonomyDefaults(current))}
                sx={{ fontWeight: 600 }}
              >
                Use defaults
              </Button>
            </Box>
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mb: 1.5 }}>
              {usesTaxonomyDefaults(current)
                ? 'Using Motzu’s defaults for every category. Change a row to override it.'
                : `${taxonomyOwnCount} ${taxonomyOwnCount === 1 ? 'category overrides' : 'categories override'} the defaults.`}
              {/* With "Don't set" on offer the claim is no longer true; the note under the select says what happens. */}
              {!unsetAllowed && ' Motzu always sets a category, so Shopify never guesses one from a product’s title.'}
            </Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5 }}>When a product matches nothing</Typography>
            <TaxonomyCategorySelect
              aria-label="Shopify category for unmapped products"
              value={unsetChosen ? UNMAPPED_CATEGORY_UNSET : unmappedFallbackGid(current) || ''}
              defaultGid={categoryDefaults.data?.neutralGid ?? null}
              defaultLabelPrefix="Neutral"
              options={unsetAllowed ? [{ gid: UNMAPPED_CATEGORY_UNSET, label: UNMAPPED_CATEGORY_UNSET_LABEL }, ...taxonomyOptions] : taxonomyOptions}
              onChange={(gid) => patch({ unmappedTaxonomyCategory: gid || undefined })}
            />
            {unsetAllowed && (
              <Typography sx={{ mt: 0.75, fontSize: 12, color: tokens.ink.muted }}>
                {unmappedCategoryNote(true, unsetChosen)}
              </Typography>
            )}
            {categoryDefaults.isError && (
              <Typography sx={{ mt: 1, fontSize: 12, color: tokens.signal.caution.ink }}>
                Could not load the Shopify category list. Rows show no default until it loads.
              </Typography>
            )}
            {unmappedFallbackGid(current) && (
              <Typography sx={{ mt: 0.75, fontSize: 12, color: tokens.ink.muted }}>
                Unmapped products sync as {taxonomyLabel(taxonomyOptions, unmappedFallbackGid(current))}.
              </Typography>
            )}
          </Box>

          <Box sx={{ ...cardSx, p: 2.25 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.25 }}>Try a product</Typography>
            <TextField
              size="small"
              fullWidth
              value={testName}
              onChange={(event) => setTestName(event.target.value)}
              placeholder="Product name"
              sx={{ mb: 1 }}
            />
            <CreatableSelect
              label=""
              aria-label="Motzu category to try"
              value={testCategory}
              options={slugs}
              onChange={setTestCategory}
              placeholder="Motzu category"
            />
            {preview && (
              <Box sx={{ mt: 1.25, px: 1.25, py: 1, borderRadius: '8px', bgcolor: tokens.surface.sunken }}>
                <Typography sx={{ fontSize: 13, color: preview.step === 'fallback' ? tokens.signal.caution.ink : tokens.ink.primary }}>
                  Synced as <Box component="span" sx={{ fontWeight: 700 }}>{preview.type}</Box>
                </Typography>
                <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
                  {preview.step === 'rule'
                    ? `Name rule “${preview.ruleKeyword}”`
                    : preview.step === 'category'
                      ? 'Category map'
                      : 'Fallback'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
        </>
      )}
    </Box>
  );
};

const UNMATCHED_QUERY_KEY = ['merchant-taxonomy-unmatched'] as const;
const UNMATCHED_PAGE_SIZE = 50;
/** The classify route takes at most 25 products per call. */
const CLASSIFY_BATCH_SIZE = 25;
/** Product | store SKU | Motzu category | subcategory | suggestion; on a phone each field stacks. */
const UNMATCHED_TABLE_COLUMNS = { xs: 'minmax(0,1fr)', md: 'minmax(0,1.1fr) 130px 110px minmax(0,1.2fr) minmax(0,1fr)' };

const leafLabel = (entry: MerchantTaxonomyEntry): string =>
  [entry.department, entry.category, entry.subcategory].filter(Boolean).join(' › ');

/**
 * The registry as the classifier's closed tree: departments, then categories, then subcategories.
 * A node id is the registry entry id where one exists (a leaf always has one); a department or
 * category the file only implied gets a stable id built from its codes.
 */
export function buildClassifierTree(entries: MerchantTaxonomyEntry[]): ClassifierTreeNode[] {
  const departments = new Map<string, { node: ClassifierTreeNode; categories: Map<string, ClassifierTreeNode> }>();
  for (const leaf of entries.filter((entry) => entry.level === 'subcategory')) {
    let dept = departments.get(leaf.departmentCode);
    if (!dept) {
      const entry = entries.find((e) => e.level === 'department' && e.departmentCode === leaf.departmentCode);
      dept = { node: { id: entry?.id || leaf.departmentCode, label: leaf.department, children: [] }, categories: new Map() };
      departments.set(leaf.departmentCode, dept);
    }
    const categoryName = leaf.category || leaf.department;
    let category = dept.categories.get(categoryName);
    if (!category) {
      const entry = entries.find((e) => e.level === 'category' && e.departmentCode === leaf.departmentCode && e.category === leaf.category);
      category = { id: entry?.id || `${leaf.departmentCode}:${categoryName}`, label: categoryName, children: [] };
      dept.categories.set(categoryName, category);
      dept.node.children!.push(category);
    }
    category.children!.push({ id: leaf.id, label: leaf.subcategory || leaf.id });
  }
  return Array.from(departments.values()).map((dept) => dept.node);
}

interface LeafSuggestion {
  /** The subcategory the classifier settled on; null when it stopped above a leaf. */
  leafId: string | null;
  confidence: number;
  /** Labels from the department down to where it stopped. */
  trail: string[];
}

/** Reads one classify result against the tree it was asked with. Null when the product could not be read. */
export function readClassifierResult(tree: ClassifierTreeNode[], result: TaxonomyClassifierResult): LeafSuggestion | null {
  if (!result.found) return null;
  let nodes = tree;
  const trail: ClassifierTreeNode[] = [];
  let confidence = 0;
  for (const answer of result.path) {
    const node = nodes.find((candidate) => candidate.id === answer.optionId);
    if (!node) break;
    trail.push(node);
    confidence = answer.confidence;
    nodes = node.children || [];
  }
  const last = trail[trail.length - 1];
  const atLeaf = result.decided && Boolean(last) && !(last.children && last.children.length > 0);
  return { leafId: atLeaf ? last.id : null, confidence, trail: trail.map((node) => node.label) };
}

/**
 * "Products without a match": products in the store's sync selection whose store SKU matches no
 * subcategory prefix. The store places each on a subcategory by hand, or (flag
 * `taxonomy_classifier_assist`) asks for suggestions, reviews them and applies the ones it accepts.
 * Nothing is assigned until the store picks or applies; the classifier only proposes.
 */
export const UnmatchedProductsSection: React.FC<{
  registry: MerchantTaxonomyRegistry;
  leaves: MerchantTaxonomyEntry[];
  integrationId?: string;
}> = ({ registry, leaves, integrationId }) => {
  const { showToast } = useToast();
  const { enabled: classifierOn } = useOrgFeatureFlag('taxonomy_classifier_assist');
  const [suggestions, setSuggestions] = useState<Record<string, LeafSuggestion | null>>({});
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [assigned, setAssigned] = useState<Set<string>>(new Set());
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);

  const query = useInfiniteQuery({
    queryKey: [...UNMATCHED_QUERY_KEY, registry.updatedAt ?? null, integrationId ?? null],
    queryFn: ({ pageParam }) =>
      integrationApiService.listUnmatchedTaxonomyProducts({ limit: UNMATCHED_PAGE_SIZE, cursor: pageParam, integrationId }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last?.nextCursor ?? undefined,
    retry: false,
  });
  const items: UnmatchedTaxonomyProduct[] = (query.data?.pages ?? [])
    .flatMap((page) => page?.items ?? [])
    .filter((item) => !assigned.has(item.productId));
  const hasMore = Boolean(query.hasNextPage);
  const leafById = useMemo(() => new Map(leaves.map((entry) => [entry.id, entry])), [leaves]);

  const markAssigned = (productIds: string[]) => {
    setAssigned((prev) => new Set([...Array.from(prev), ...productIds]));
    setChecked((prev) => new Set(Array.from(prev).filter((id) => !productIds.includes(id))));
  };

  const pick = async (item: UnmatchedTaxonomyProduct, entryId: string) => {
    if (!entryId) return;
    setSaving(true);
    try {
      await integrationApiService.assignTaxonomyProducts([{ productId: item.productId, entryId, source: 'manual' }]);
      markAssigned([item.productId]);
      showToast(`${item.name} placed in ${leafById.get(entryId)?.subcategory || entryId}`, 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save the assignment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const suggest = async () => {
    const batch = items.filter((item) => !(item.productId in suggestions)).slice(0, CLASSIFY_BATCH_SIZE);
    if (batch.length === 0) {
      showToast('Every product listed already has a suggestion', 'info');
      return;
    }
    const tree = buildClassifierTree(registry.entries);
    setSuggesting(true);
    try {
      const response = await integrationApiService.classifyTaxonomyProducts(batch.map((item) => item.productId), tree);
      const next: Record<string, LeafSuggestion | null> = {};
      for (const result of response.results) next[result.productId] = readClassifierResult(tree, result);
      setSuggestions((prev) => ({ ...prev, ...next }));
    } catch (err: unknown) {
      // A 429 carries the daily budget message; show it as the server wrote it.
      showToast(err instanceof Error ? err.message : 'Failed to suggest categories', 'error');
    } finally {
      setSuggesting(false);
    }
  };

  const applySelected = async () => {
    const assignments = Array.from(checked).flatMap((productId) => {
      const suggestion = suggestions[productId];
      // A suggestion made against a registry that has since been replaced may name a leaf that no longer exists.
      return suggestion?.leafId && leafById.has(suggestion.leafId)
        ? [{ productId, entryId: suggestion.leafId, source: 'classifier' as const, confidence: suggestion.confidence }]
        : [];
    });
    if (assignments.length === 0) return;
    setSaving(true);
    try {
      await integrationApiService.assignTaxonomyProducts(assignments);
      markAssigned(assignments.map((a) => a.productId));
      showToast(`Applied ${assignments.length} suggestion${assignments.length === 1 ? '' : 's'}`, 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to apply the suggestions', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (productId: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });

  return (
    <Box sx={{ px: 2.25, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1, borderTop: '1px solid', borderColor: tokens.line.hairline }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            Products without a match{query.isSuccess ? ` · ${items.length}${hasMore ? '+' : ''}` : ''}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, maxWidth: 640 }}>
            Selected products whose store SKU matches no subcategory prefix. Pick a subcategory and that product follows its Shopify category and product type.
          </Typography>
        </Box>
        {classifierOn && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              disabled={suggesting || saving || items.length === 0}
              startIcon={suggesting ? <CircularProgress size={14} /> : undefined}
              onClick={suggest}
              sx={{ height: 34, borderColor: tokens.line.default, color: tokens.ink.body, fontWeight: 600 }}
            >
              Suggest categories
            </Button>
            <Button
              variant="contained"
              disabled={saving || checked.size === 0}
              onClick={applySelected}
              sx={{ height: 34, fontWeight: 700 }}
            >
              Apply selected{checked.size > 0 ? ` (${checked.size})` : ''}
            </Button>
          </Box>
        )}
      </Box>

      {query.isLoading && <CircularProgress size={18} />}
      {query.isError && (
        <Typography sx={{ fontSize: 13, color: tokens.signal.caution.ink }}>
          {query.error instanceof Error ? query.error.message : 'Failed to load products without a match'}
        </Typography>
      )}
      {query.isSuccess && items.length === 0 && !hasMore && (
        <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>Every selected product matches a subcategory.</Typography>
      )}

      {items.length > 0 && (
        <Box sx={{ maxHeight: 420, overflow: 'auto', border: '1px solid', borderColor: tokens.line.hairline, borderRadius: '8px' }}>
          {items.map((item) => {
            const suggestion = suggestions[item.productId];
            const asked = item.productId in suggestions;
            const leaf = suggestion?.leafId ? leafById.get(suggestion.leafId) : undefined;
            return (
              <Box
                key={item.productId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: UNMATCHED_TABLE_COLUMNS,
                  columnGap: 1,
                  rowGap: 0.5,
                  alignItems: 'center',
                  px: 1.25,
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: tokens.line.hairline,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap title={item.name}>{item.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.ink.muted }} noWrap>{item.brand}</Typography>
                </Box>
                <Typography sx={{ ...mono }} noWrap title={item.storeSku}>{item.storeSku || '—'}</Typography>
                <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }} noWrap title={item.motzuCategory}>{item.motzuCategory || '—'}</Typography>
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value=""
                  disabled={saving}
                  onChange={(event) => { void pick(item, String(event.target.value)); }}
                  inputProps={{ 'aria-label': `Subcategory for ${item.name}` }}
                  renderValue={() => 'Choose subcategory'}
                  sx={{ fontSize: 13, '& .MuiSelect-select': { py: 0.75 } }}
                >
                  {leaves.map((entry) => (
                    <MenuItem key={entry.id} value={entry.id}>{leafLabel(entry)}</MenuItem>
                  ))}
                </Select>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  {asked && suggestion === null && (
                    <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>No suggestion available</Typography>
                  )}
                  {suggestion && leaf && (
                    <>
                      <Checkbox
                        size="small"
                        checked={checked.has(item.productId)}
                        onChange={() => toggle(item.productId)}
                        inputProps={{ 'aria-label': `Apply suggestion for ${item.name}` }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap title={leafLabel(leaf)}>{leaf.subcategory}</Typography>
                        <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{Math.round(suggestion.confidence * 100)}% confident</Typography>
                      </Box>
                    </>
                  )}
                  {suggestion && !leaf && (
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.signal.caution.ink }}>Needs review</Typography>
                      <Typography sx={{ fontSize: 12, color: tokens.ink.muted }} noWrap>
                        {suggestion.trail.length > 0 ? `Narrowed to ${suggestion.trail.join(' › ')}` : 'No category fits'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {hasMore && (
        <Box>
          <Button
            variant="text"
            size="small"
            disabled={query.isFetchingNextPage}
            onClick={() => { void query.fetchNextPage(); }}
            sx={{ fontWeight: 600 }}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more products'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

type TaxonomyDraft = Partial<Pick<MerchantTaxonomyEntry, 'skuPrefix' | 'shopifyCategoryGid' | 'productType'>>;

/**
 * "Your taxonomy": the store's uploaded department / category / subcategory registry. Saves on its
 * own (not through the integration's settings), because it belongs to the store, not one integration.
 */
const MerchantTaxonomyCard: React.FC<{
  registry: MerchantTaxonomyRegistry | null;
  stats: MerchantTaxonomyStats | undefined;
  loading: boolean;
  taxonomyOptions: Array<{ gid: string; label: string }>;
  typeOptions: string[];
  /** Flag `structured_mapping_inputs`: the selection's products, for prefix validation and suggestions. */
  indexRows?: MappingIndexRow[];
  integrationId?: string;
}> = ({ registry, stats, loading, taxonomyOptions: baseTaxonomyOptions, typeOptions, indexRows, integrationId }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useAppTranslation();
  const fileInput = React.useRef<HTMLInputElement>(null);
  const [drafts, setDrafts] = useState<Record<string, TaxonomyDraft>>({});
  // The parent labels stored GIDs; a draft picked from the full taxonomy is labelled here.
  const taxonomyOptions = useShopifyTaxonomyOptions(
    baseTaxonomyOptions,
    useMemo(() => Object.values(drafts).map((draft) => draft.shopifyCategoryGid), [drafts]),
  );
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [trySku, setTrySku] = useState('');
  const [tryResult, setTryResult] = useState<{ entry: MerchantTaxonomyEntry | null } | null>(null);
  // A file that would delete subcategories waits here for the store's confirmation.
  const [pendingImport, setPendingImport] = useState<{ file: File; preview: MerchantTaxonomyImportPreview } | null>(null);

  const leaves = useMemo(() => (registry?.entries || []).filter((entry) => entry.level === 'subcategory'), [registry]);
  const needle = filter.trim().toLowerCase();
  const visible = leaves.filter((entry) =>
    !needle || [entry.department, entry.category, entry.subcategory, entry.subcategoryCode, entry.skuPrefix]
      .some((value) => String(value || '').toLowerCase().includes(needle)),
  );
  const dirtyIds = Object.keys(drafts);

  // Flag `structured_mapping_inputs`: leading SKU segments in the selection that no subcategory claims yet.
  const prefixOptions = useMemo(() => {
    if (!indexRows) return [];
    const claimed = leaves.map((entry) => drafts[entry.id]?.skuPrefix ?? entry.skuPrefix ?? '');
    return suggestSkuPrefixes(indexRows, claimed).map(({ prefix: value, count }) => ({ value, hint: `${count} SKU${count === 1 ? '' : 's'} in your selection` }));
  }, [indexRows, leaves, drafts]);
  const matchCount = (prefix: string) => (indexRows ? countPrefixMatches(indexRows, prefix) : 0);

  const edit = (id: string, change: TaxonomyDraft) => setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...change } }));
  const apply = (data: { registry: MerchantTaxonomyRegistry; stats: MerchantTaxonomyStats }) =>
    queryClient.setQueryData(MERCHANT_TAXONOMY_QUERY_KEY, { registry: data.registry, stats: data.stats });

  const resetFileInput = () => {
    if (fileInput.current) fileInput.current.value = '';
  };

  /** Checks the file first (dry run). A file that removes subcategories asks before it replaces the registry. */
  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const preview = await integrationApiService.previewMerchantTaxonomyImport(file);
      if (preview.wouldRemove.leaves > 0) {
        setPendingImport({ file, preview });
        return;
      }
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to check the taxonomy file', 'error');
      resetFileInput();
      return;
    } finally {
      setBusy(false);
    }
    await upload(file);
  };

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const result = await integrationApiService.importMerchantTaxonomy(file);
      apply(result);
      setDrafts({});
      const warnings = result.warnings || [];
      const stripped = warnings.length > 0
        ? ` ${t('csvTemplate.taxonomy.prefixStripped', { count: warnings.length, row: warnings[0].row, message: warnings[0].message })}`
        : '';
      showToast(
        (result.errors.length > 0
          ? `Imported ${result.stats.leaves} subcategories; ${result.errors.length} row${result.errors.length === 1 ? '' : 's'} skipped (row ${result.errors[0].row}: ${result.errors[0].message})`
          : `Imported ${result.stats.leaves} subcategories`) + stripped,
        result.errors.length > 0 || warnings.length > 0 ? 'warning' : 'success',
      );
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to import the taxonomy', 'error');
    } finally {
      setBusy(false);
      resetFileInput();
    }
  };

  /** Rejects on failure (after the toast) so the app-update dialog stays open instead of refreshing over the edits. */
  const persist = async () => {
    const patches: MerchantTaxonomyPatch[] = dirtyIds.map((id) => ({ id, ...drafts[id] }));
    setBusy(true);
    try {
      apply(await integrationApiService.updateMerchantTaxonomyEntries(patches));
      setDrafts({});
      showToast('Your taxonomy was saved', 'success');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save your taxonomy', 'error');
      throw err;
    } finally {
      setBusy(false);
    }
  };
  const save = () => { persist().catch(() => undefined); };

  // The card's draft edits are their own save surface; lets the app-update prompt wait for, save or drop them.
  useUnsavedChangesSource({ dirty: dirtyIds.length > 0, save: persist, discard: () => setDrafts({}) });

  const tryIt = async () => {
    if (!trySku.trim()) return;
    try {
      setTryResult(await integrationApiService.resolveMerchantTaxonomy({ storeSku: trySku.trim() }));
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to resolve the SKU', 'error');
    }
  };

  const optionLabel = (gid?: string) => taxonomyOptions.find((option) => option.gid === gid)?.label;

  return (
    <Box sx={{ ...cardSx, overflow: 'hidden' }}>
      <ConfirmDialog
        open={Boolean(pendingImport)}
        title="Replace your taxonomy?"
        message={
          pendingImport
            ? `This file removes ${pendingImport.preview.wouldRemove.leaves} subcategories (${pendingImport.preview.wouldRemove.mapped} of them mapped). Continue?`
            : ''
        }
        preview={
          pendingImport && pendingImport.preview.wouldRemove.ids.length > 0 ? (
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 1.5 }}>
              Removed: {pendingImport.preview.wouldRemove.ids.join(', ')}
              {pendingImport.preview.wouldRemove.leaves > pendingImport.preview.wouldRemove.ids.length ? ', …' : ''}
              {(pendingImport.preview.wouldDropAssignments ?? 0) > 0
                ? ` ${pendingImport.preview.wouldDropAssignments} product assignment${pendingImport.preview.wouldDropAssignments === 1 ? '' : 's'} on them will be dropped.`
                : ''}
            </Typography>
          ) : undefined
        }
        confirmLabel="Replace"
        confirmColor="warning"
        loading={busy}
        onClose={() => {
          setPendingImport(null);
          resetFileInput();
        }}
        onConfirm={() => {
          const file = pendingImport?.file;
          setPendingImport(null);
          if (file) void upload(file);
        }}
      />
      <Box sx={{ px: 2.25, pt: 2, pb: 1.25, display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Your taxonomy</Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink.muted, maxWidth: 640, mt: 0.5 }}>
            Upload your department, category and subcategory list. A product&apos;s store SKU (like CAM-121-00001) picks its entry, and that entry&apos;s Shopify category and product type win over everything below.
            A matching entry&apos;s category replaces the one already on the listing in Enrich, unless the Category owner is set to Fill only when blank or Never touch. A new upload replaces this list.
          </Typography>
          {registry && (
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, maxWidth: 640, mt: 0.5 }}>{t('csvTemplate.taxonomy.codePrefix')}</Typography>
          )}
          {registry && (
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.faint, mt: 0.5 }}>
              {registry.importedFileName ? `${registry.importedFileName} · ` : ''}
              {registry.importedAt ? `imported ${new Date(registry.importedAt).toLocaleDateString()}` : 'entered by hand'}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <input
            ref={fileInput}
            type="file"
            accept=".csv,text/csv"
            hidden
            data-testid="taxonomy-file"
            onChange={(event) => pickFile(event.target.files?.[0])}
          />
          {!registry && !loading && <CsvTemplateHint compact size="medium" {...taxonomyTemplateProps} intro={t('csvTemplate.taxonomy.intro')} />}
          <Button
            variant="outlined"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
            sx={{ height: 36, borderColor: tokens.line.default, borderRadius: '9px', color: tokens.ink.body, fontWeight: 600 }}
          >
            {registry ? 'Upload a new CSV' : 'Upload CSV'}
          </Button>
          <Button variant="contained" disabled={busy || dirtyIds.length === 0} onClick={save} sx={{ height: 36, borderRadius: '9px', fontWeight: 700 }}>
            Save taxonomy{dirtyIds.length > 0 ? ` (${dirtyIds.length})` : ''}
          </Button>
        </Box>
      </Box>

      {loading && <Box sx={{ px: 2.25, pb: 2 }}><CircularProgress size={18} /></Box>}
      {!loading && !registry && (
        <Box sx={{ px: 2.25, pb: 2 }}>
          <Typography sx={{ fontSize: 13.5, color: tokens.ink.muted, mb: 1 }}>
            No taxonomy uploaded yet. The CSV needs the columns Department, Code, Category, Subcategory and Subcategory Code.
          </Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink.muted, mb: 0.5 }}>{t('csvTemplate.taxonomy.codePrefix')}</Typography>
          <Typography sx={{ fontSize: 13, color: tokens.ink.muted, mb: 1 }}>{t('csvTemplate.taxonomy.optionalColumns')}</Typography>
          <CsvTemplateHint explainerOnly {...taxonomyTemplateProps} intro={t('csvTemplate.taxonomy.intro')} />
        </Box>
      )}

      {registry && (
        <>
          <Box sx={{ px: 2.25, pb: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
              {stats?.mapped ?? 0} of {stats?.leaves ?? leaves.length} subcategories mapped
            </Typography>
            <TextField
              size="small"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Filter by department, category or code"
              inputProps={{ 'aria-label': 'Filter taxonomy' }}
              sx={{ flex: '1 1 220px', '& .MuiInputBase-root': { height: 34 } }}
            />
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: TAXONOMY_TABLE_COLUMNS, px: 2.25, height: 34, alignItems: 'center', bgcolor: tokens.surface.sunken, columnGap: 1 }}>
            {['Subcategory', 'SKU prefix', 'Shopify category', 'Product type'].map((label) => (
              <Typography key={label} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: tokens.ink.muted }}>
                {label}
              </Typography>
            ))}
          </Box>
          <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
            {visible.length === 0 && (
              <Typography sx={{ px: 2.25, py: 2, fontSize: 13.5, color: tokens.ink.muted }}>No subcategory matches “{filter.trim()}”.</Typography>
            )}
            {visible.map((entry) => {
              const draft = drafts[entry.id] || {};
              const prefix = draft.skuPrefix ?? entry.skuPrefix ?? '';
              const gid = draft.shopifyCategoryGid ?? entry.shopifyCategoryGid ?? '';
              const type = draft.productType ?? entry.productType ?? '';
              return (
                <Box
                  key={entry.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: TAXONOMY_TABLE_COLUMNS,
                    columnGap: 1,
                    rowGap: 0.75,
                    alignItems: 'center',
                    px: 2.25,
                    py: 0.75,
                    borderBottom: '1px solid',
                    borderColor: tokens.line.hairline,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap title={entry.subcategory}>{entry.subcategory}</Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }} noWrap>
                      {entry.department} › {entry.category}{entry.subcategoryCode ? ` · ${entry.subcategoryCode}` : ''}
                    </Typography>
                  </Box>
                  {indexRows ? (
                    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start', '& input': mono }}>
                      <CreatableSelect
                        label=""
                        aria-label={`SKU prefix for ${entry.subcategory}`}
                        value={prefix}
                        options={prefixOptions}
                        onChange={(value) => edit(entry.id, { skuPrefix: value })}
                        sx={{ width: '100%' }}
                      />
                      {prefix.trim() && (
                        matchCount(prefix) > 0
                          ? <StatusChip label={`matches ${matchCount(prefix)}`} tone="success" />
                          : <StatusChip label="matches nothing" tone="warning" />
                      )}
                    </Box>
                  ) : (
                    <TextField
                      size="small"
                      value={prefix}
                      onChange={(event) => edit(entry.id, { skuPrefix: event.target.value })}
                      inputProps={{ 'aria-label': `SKU prefix for ${entry.subcategory}`, style: mono }}
                    />
                  )}
                  <ShopifyCategoryPicker
                    aria-label={`Shopify category for ${entry.subcategory}`}
                    value={gid}
                    emptyLabel="Not mapped"
                    options={taxonomyOptions}
                    onChange={(value) => edit(entry.id, { shopifyCategoryGid: value })}
                  />
                  <CreatableSelect
                    label=""
                    aria-label={`Product type for ${entry.subcategory}`}
                    value={type}
                    options={typeOptions}
                    onChange={(value) => edit(entry.id, { productType: value })}
                    placeholder="Product type"
                  />
                </Box>
              );
            })}
          </Box>

          <Box sx={{ px: 2.25, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Try a SKU</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                value={trySku}
                onChange={(event) => { setTrySku(event.target.value); setTryResult(null); }}
                onKeyDown={(event) => { if (event.key === 'Enter') tryIt(); }}
                placeholder="CAM-121-00001"
                inputProps={{ 'aria-label': 'SKU to try', style: mono }}
                sx={{ flex: '1 1 220px', '& .MuiInputBase-root': { height: 34 } }}
              />
              <Button variant="outlined" disabled={!trySku.trim()} onClick={tryIt} sx={{ height: 34, fontWeight: 600 }}>
                Try
              </Button>
            </Box>
            {tryResult && (
              <Box sx={{ px: 1.25, py: 1, borderRadius: '8px', bgcolor: tokens.surface.sunken }}>
                {tryResult.entry ? (
                  <>
                    <Typography sx={{ fontSize: 13 }}>
                      Matches <Box component="span" sx={{ fontWeight: 700 }}>{tryResult.entry.department} › {tryResult.entry.category} › {tryResult.entry.subcategory}</Box>
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
                      {optionLabel(tryResult.entry.shopifyCategoryGid) || 'No Shopify category'} · {tryResult.entry.productType || 'No product type'}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 13, color: tokens.signal.caution.ink }}>No entry matches. This SKU resolves from the maps below.</Typography>
                )}
              </Box>
            )}
          </Box>

          {leaves.length > 0 && (
            <UnmatchedProductsSection
              key={registry.updatedAt ?? ''}
              registry={registry}
              leaves={leaves}
              integrationId={integrationId}
            />
          )}
        </>
      )}
    </Box>
  );
};

const ResolutionCard: React.FC<{ n: string; title: string; body: string; tone: 'info' | 'caution' }> = ({
  n,
  title,
  body,
  tone,
}) => {
  const signal = tokens.signal[tone];
  return (
    <Box sx={{ display: 'flex', gap: 1.25, p: 1.5, borderRadius: '12px', border: '1px solid', borderColor: tokens.line.default, bgcolor: tokens.surface.sunken }}>
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, bgcolor: signal.tint, color: signal.ink, flex: '0 0 auto' }}>
        {n}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{body}</Typography>
      </Box>
    </Box>
  );
};

const SUMMARY_QUERY_KEY = ['shopify-category-summary'] as const;
const SIMPLE_ROW_COLUMNS = { xs: 'minmax(0,1fr)', md: 'minmax(0,1.2fr) 130px minmax(0,1.2fr) 150px' };

/**
 * The simple view of the Mapping tab (flag `shopify_mapping_simple_mode`): one sentence, how many products
 * have a category, and the few that need a look. A fix writes a per-product assignment when the store has a
 * registry, or a per-product Shopify category (`storeLayout.productCategoryOverrides`) when it has none.
 * The override travels with the integration's settings, so it applies when the parent saves.
 */
const SimpleCategoryCard: React.FC<{
  integrationId: string;
  layout: StoreLayoutSettings;
  onChange: (partial: Partial<StoreLayoutSettings>) => void;
  registry: MerchantTaxonomyRegistry | null;
  taxonomyOptions: Array<{ gid: string; label: string }>;
  neutralGid?: string;
  ownershipShown: boolean;
  advancedOpen: boolean;
  onSetView: (view: 'simple' | 'advanced') => void;
}> = ({ integrationId, layout, onChange, registry, taxonomyOptions, neutralGid, ownershipShown, advancedOpen, onSetView }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { enabled: classifierOn } = useOrgFeatureFlag('taxonomy_classifier_assist');
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [confidence, setConfidence] = useState<Record<string, number>>({});
  const [done, setDone] = useState<Set<string>>(new Set());
  const [savingId, setSavingId] = useState<string | null>(null);
  const [suggesting, setSuggesting] = useState(false);

  // Set after a save so the next read recomputes on the server instead of serving its 60 s cache; cleared once used.
  const refreshNext = useRef(false);
  const markSummaryStale = () => {
    refreshNext.current = true;
    // Mark stale without refetching now: the local adjustments already drop the row, and a refetch before the
    // parent saves the layout would bring an override-fixed row straight back.
    void queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY, refetchType: 'none' });
    void queryClient.invalidateQueries({ queryKey: MAPPING_INDEX_QUERY_KEY, refetchType: 'none' });
  };

  const summary = useQuery({
    queryKey: [...SUMMARY_QUERY_KEY, integrationId],
    queryFn: () => {
      if (!refreshNext.current) return integrationApiService.getShopifyCategorySummary(integrationId);
      refreshNext.current = false;
      return integrationApiService.getShopifyCategorySummary(integrationId, { refresh: true });
    },
    retry: false,
  });
  const data: ShopifyCategorySummary | undefined = summary.data;

  const leaves = useMemo(() => (registry?.entries ?? []).filter((entry) => entry.level === 'subcategory'), [registry]);
  // With no registry the choices are Shopify's own categories; the neutral top level is what a product already gets.
  const shopifyChoices = useMemo(() => taxonomyOptions.filter((option) => option.gid !== neutralGid), [taxonomyOptions, neutralGid]);
  const options = registry
    ? leaves.map((entry) => ({ value: entry.id, label: leafLabel(entry) }))
    : shopifyChoices.map((option) => ({ value: option.gid, label: option.label }));

  const rows = (data?.needsLook ?? []).filter((row) => !done.has(row.productId));
  const total = data?.total ?? 0;
  const needs = Math.max((data ? data.bySource.store_fallback + data.bySource.neutral : 0) - done.size, 0);
  const categorised = Math.max(total - needs, 0);
  const sources = data
    ? [
      { label: 'Your taxonomy', count: data.bySource.merchant_taxonomy },
      { label: 'Set by hand', count: data.bySource.product_override + done.size },
      { label: 'Name rules', count: data.bySource.name_rule },
      { label: 'Map', count: data.bySource.store_map + data.bySource.default_map },
      { label: 'Inferred', count: data.bySource.inferred },
    ].filter((source) => source.count > 0)
    : [];

  const ownerLabel = (owner: ShopifyFieldOwner | undefined): string =>
    OWNER_CHOICES.find((choice) => choice.value === (owner || 'motzu'))?.label ?? 'Motzu owns';
  const categoryOwner = layout.shopifyCategoryOwner ?? data?.owners.category;
  const typeOwner = layout.shopifyProductTypeOwner ?? data?.owners.productType;

  const save = async (row: ShopifyCategorySummary['needsLook'][number]) => {
    const value = choices[row.productId];
    if (!value) return;
    setSavingId(row.productId);
    try {
      if (registry) {
        const picked = confidence[row.productId];
        await integrationApiService.assignTaxonomyProducts([
          { productId: row.productId, entryId: value, source: picked !== undefined ? 'classifier' : 'manual', ...(picked !== undefined ? { confidence: picked } : {}) },
        ]);
        void queryClient.invalidateQueries({ queryKey: UNMATCHED_QUERY_KEY });
        markSummaryStale();
        showToast(`${row.name} placed`, 'success');
      } else {
        onChange({ productCategoryOverrides: { ...(layout.productCategoryOverrides || {}), [row.productId]: value } });
        markSummaryStale();
        showToast(`${row.name} set. Save your changes to apply it.`, 'success');
      }
      setDone((prev) => new Set([...Array.from(prev), row.productId]));
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save the category', 'error');
    } finally {
      setSavingId(null);
    }
  };

  const suggest = async () => {
    const batch = rows.filter((row) => !(row.productId in choices)).slice(0, CLASSIFY_BATCH_SIZE);
    if (batch.length === 0) {
      showToast('Every product listed already has a choice', 'info');
      return;
    }
    // A registry is the closed tree; without one, Shopify's own categories are offered as a flat, one-level tree.
    const tree: ClassifierTreeNode[] = registry
      ? buildClassifierTree(registry.entries)
      : shopifyChoices.map((option) => ({ id: option.gid, label: option.label }));
    if (tree.length === 0) return;
    setSuggesting(true);
    try {
      const response = await integrationApiService.classifyTaxonomyProducts(batch.map((row) => row.productId), tree);
      const nextChoices: Record<string, string> = {};
      const nextConfidence: Record<string, number> = {};
      for (const result of response.results) {
        const read = readClassifierResult(tree, result);
        if (read?.leafId) {
          nextChoices[result.productId] = read.leafId;
          nextConfidence[result.productId] = read.confidence;
        }
      }
      setChoices((prev) => ({ ...prev, ...nextChoices }));
      setConfidence((prev) => ({ ...prev, ...nextConfidence }));
      if (Object.keys(nextChoices).length === 0) showToast('No confident suggestion for these products', 'info');
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to suggest categories', 'error');
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <Box sx={{ ...cardSx, px: 2.75, py: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: tokens.ink.primary }}>Motzu sets Shopify categories for you.</Typography>
        {summary.isLoading && <CircularProgress size={16} sx={{ mt: 1 }} />}
        {summary.isError && (
          <Typography sx={{ fontSize: 13.5, color: tokens.signal.caution.ink, mt: 0.5 }}>
            {summary.error instanceof Error ? summary.error.message : 'Failed to load the category summary'}
          </Typography>
        )}
        {data && total > 0 && (
          <Typography sx={{ fontSize: 13.5, color: tokens.ink.muted, mt: 0.5 }}>
            {`${categorised} of ${total} products categorised · ${needs === 0 ? 'none need a look' : `${needs} need a look`}`}
            {data.truncated ? ' (first 2,000 selected products)' : ''}
          </Typography>
        )}
      </Box>

      {data && total === 0 && (
        <EmptyState size="compact" title="No products are selected to sync yet" description="Pick products to sync and their categories show up here." />
      )}

      {sources.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }} aria-label="Where categories come from">
          {sources.map((source) => (
            <StatusChip key={source.label} label={`${source.label} ${source.count}`} tone="neutral" />
          ))}
        </Box>
      )}

      {rows.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Need a look</Typography>
            {classifierOn && (
              <Button
                variant="outlined"
                disabled={suggesting || savingId !== null}
                startIcon={suggesting ? <CircularProgress size={14} /> : undefined}
                onClick={suggest}
                sx={{ height: 34, borderColor: tokens.line.default, color: tokens.ink.body, fontWeight: 600 }}
              >
                Suggest
              </Button>
            )}
          </Box>
          <Box sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: tokens.line.hairline, borderRadius: '8px' }}>
            {rows.map((row) => (
              <Box
                key={row.productId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: SIMPLE_ROW_COLUMNS,
                  columnGap: 1,
                  rowGap: 0.5,
                  alignItems: 'center',
                  px: 1.25,
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: tokens.line.hairline,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap title={row.name}>{row.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.ink.muted }} noWrap>
                    {row.brand}{row.suggestedLeafLabel ? ` · now ${row.suggestedLeafLabel}` : ''}
                  </Typography>
                </Box>
                <Typography sx={{ ...mono }} noWrap title={row.storeSku}>{row.storeSku || '—'}</Typography>
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value={choices[row.productId] ?? ''}
                  disabled={savingId !== null}
                  onChange={(event) => {
                    const value = String(event.target.value);
                    setChoices((prev) => ({ ...prev, [row.productId]: value }));
                    // A hand pick replaces a suggestion, and with it the classifier's confidence.
                    setConfidence((prev) => {
                      const { [row.productId]: _dropped, ...rest } = prev;
                      return rest;
                    });
                  }}
                  inputProps={{ 'aria-label': `${registry ? 'Subcategory' : 'Shopify category'} for ${row.name}` }}
                  renderValue={(selected) =>
                    selected ? options.find((option) => option.value === selected)?.label ?? String(selected) : registry ? 'Choose subcategory' : 'Choose category'}
                  sx={{ fontSize: 13, '& .MuiSelect-select': { py: 0.75 } }}
                >
                  {options.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!choices[row.productId] || savingId !== null}
                    onClick={() => { void save(row); }}
                    aria-label={`Save category for ${row.name}`}
                    sx={{ fontWeight: 700 }}
                  >
                    Save
                  </Button>
                  {row.productId in confidence && (
                    <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{Math.round(confidence[row.productId] * 100)}%</Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {ownershipShown && (
        <Typography sx={{ fontSize: 13, color: tokens.ink.muted }}>
          <Box component="span" sx={{ fontWeight: 600, color: tokens.ink.body }}>Who owns these fields:</Box>
          {` Shopify Category: ${ownerLabel(categoryOwner)} · Product Type: ${ownerLabel(typeOwner)}. `}
          {!advancedOpen && (
            <Button size="small" variant="text" onClick={() => onSetView('advanced')} sx={{ fontWeight: 600, minWidth: 0, p: 0, verticalAlign: 'baseline' }}>
              Change
            </Button>
          )}
        </Typography>
      )}

      <Box>
        <Button size="small" variant="text" onClick={() => onSetView(advancedOpen ? 'simple' : 'advanced')} sx={{ fontWeight: 600, px: 0 }}>
          {advancedOpen ? 'Hide advanced settings' : 'Advanced settings'}
        </Button>
      </Box>
    </Box>
  );
};

const OWNER_CHOICES: Array<{ value: ShopifyFieldOwner; label: string }> = [
  { value: 'motzu', label: 'Motzu owns' },
  { value: 'fill_blank', label: 'Fill only when blank' },
  { value: 'never', label: 'Never touch' },
];

/** Three-way owner choice for one Shopify field; a missing value shows as Motzu owns (what the sync does until the store chooses). */
export const OwnerChoice: React.FC<{
  label: string;
  value: ShopifyFieldOwner | undefined;
  onChange: (owner: ShopifyFieldOwner) => void;
}> = ({ label, value, onChange }) => (
  <Box sx={{ mb: 1.25 }}>
    <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5 }}>{label}</Typography>
    <ToggleButtonGroup
      exclusive
      size="small"
      fullWidth
      value={value || 'motzu'}
      aria-label={`${label} owner`}
      onChange={(_event, next: ShopifyFieldOwner | null) => {
        if (next) onChange(next);
      }}
    >
      {OWNER_CHOICES.map((choice) => (
        <ToggleButton key={choice.value} value={choice.value} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12.5 }}>
          {choice.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
);

const FallbackChoice: React.FC<{
  selected: boolean;
  title: string;
  body: string;
  onSelect: () => void;
  children?: React.ReactNode;
}> = ({ selected, title, body, onSelect, children }) => (
  <Box
    onClick={onSelect}
    sx={{
      mb: 1,
      p: 1.25,
      borderRadius: '10px',
      border: '1px solid',
      borderColor: selected ? tokens.accent.border : tokens.line.default,
      bgcolor: selected ? tokens.signal.info.row : tokens.surface.card,
      cursor: 'pointer',
    }}
  >
    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted }}>{body}</Typography>
    {children && <Box onClick={(event) => event.stopPropagation()}>{children}</Box>}
  </Box>
);

export default ShopifyCategoryMappingSection;
